/**
 * @fileoverview Deposits Portfolio — Cost of Deposit Composition page.
 * @description Ported from the InsightBox Banking design system's own
 * `DepositsPortfolio/CostOfDeposit.tsx` — two paired pie breakdowns (deposit portfolio mix vs.
 * interest-paid mix) side by side via `PieBreakdownCardGroup`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  depositMix,
  formatCr,
  resolveBankingAccessScope,
  resolveFy,
  totalInterestPaid,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { PieBreakdownCardGroup } from '@/shared/components/molecules/PieBreakdownCard'
import { categoricalPalette } from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { PieGroup } from '@/shared/components/molecules/PieBreakdownCard'

const [SEGMENT_COLOR_TERM, SEGMENT_COLOR_SAVINGS, SEGMENT_COLOR_CURRENT] = categoricalPalette(3)
const SEGMENT_COLORS: Record<string, string> = {
  term: SEGMENT_COLOR_TERM!,
  savings: SEGMENT_COLOR_SAVINGS!,
  current: SEGMENT_COLOR_CURRENT!,
}

/**
 * @component
 */
function BankingDepositsCostOfDepositPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.depositsCostOfDeposit.kicker')}
      title={t('banking.depositsCostOfDeposit.title')}
      description={t('banking.depositsCostOfDeposit.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const mix = depositMix(data, fy, branchId)
  const totalDeposits = mix.reduce((sum, m) => sum + m.amountCr, 0)
  const totalInterest = totalInterestPaid(data, fy, branchId)
  const interestBearingSharePct =
    (mix.filter((m) => m.key !== 'current').reduce((sum, m) => sum + m.amountCr, 0) /
      totalDeposits) *
    100
  const term = mix.find((m) => m.key === 'term')!
  const savings = mix.find((m) => m.key === 'savings')!

  const groups: PieGroup[] = [
    {
      key: 'portfolio',
      label: t('banking.depositsCostOfDeposit.groups.portfolio.label'),
      sublabel: t('banking.depositsCostOfDeposit.groups.total', { value: formatCr(totalDeposits) }),
      categoryColumnLabel: t('banking.depositsCostOfDeposit.groups.depositTypeColumn'),
      valueColumnLabel: t('banking.depositsCostOfDeposit.groups.portfolio.valueColumn'),
      segments: mix.map((m) => ({
        key: m.key,
        label: m.label,
        value: m.amountCr,
        pct: m.depositSharePct,
        color: SEGMENT_COLORS[m.key]!,
      })),
    },
    {
      key: 'interest',
      label: t('banking.depositsCostOfDeposit.groups.interest.label'),
      sublabel: t('banking.depositsCostOfDeposit.groups.total', { value: formatCr(totalInterest) }),
      categoryColumnLabel: t('banking.depositsCostOfDeposit.groups.depositTypeColumn'),
      valueColumnLabel: t('banking.depositsCostOfDeposit.groups.interest.valueColumn'),
      // Keep every segment (even zero-interest Current Deposits) so its legend still shows Current
      // explicitly costs ₹0 — the pie/label renderer skips drawing a callout for a literal zero
      // value, but the legend row is unconditional.
      segments: mix.map((m) => ({
        key: m.key,
        label: m.label,
        value: m.interestCr,
        pct: m.interestSharePct,
        color: SEGMENT_COLORS[m.key]!,
      })),
    },
  ]

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.depositsCostOfDeposit.kpi.totalInterestPaid'),
      value: formatCr(totalInterest),
      sublabel: t('banking.depositsCostOfDeposit.kpi.acrossAllDepositTypes'),
      highlight: true,
    },
    {
      label: t('banking.depositsCostOfDeposit.kpi.interestBearingDeposits'),
      value: `${interestBearingSharePct.toFixed(2)}%`,
      sublabel: t('banking.depositsCostOfDeposit.kpi.shareOfTotalDeposits'),
    },
    {
      label: t('banking.depositsCostOfDeposit.kpi.interestOnTerm'),
      value: `${term.interestSharePct.toFixed(2)}%`,
      sublabel: t('banking.depositsCostOfDeposit.kpi.ofTotal', {
        part: formatCr(term.interestCr),
        total: formatCr(totalInterest),
      }),
    },
    {
      label: t('banking.depositsCostOfDeposit.kpi.interestOnSavings'),
      value: `${savings.interestSharePct.toFixed(2)}%`,
      sublabel: formatCr(savings.interestCr),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        title={t('banking.depositsCostOfDeposit.chartTitle')}
        subtitle={t('banking.depositsCostOfDeposit.chartSubtitle', {
          branch: branchLabel(branchId),
          fy,
        })}
      >
        <PieBreakdownCardGroup
          groups={groups}
          valueFormatter={(v) => formatCr(v)}
          totalLabel={t('banking.total')}
        />
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.depositsCostOfDeposit.businessSummaryTitle', { fy })}
        lines={[
          t('banking.depositsCostOfDeposit.summary.line1', {
            depositShare: term.depositSharePct.toFixed(2),
            interestShare: term.interestSharePct.toFixed(2),
            interestAmount: formatCr(term.interestCr),
          }),
          t('banking.depositsCostOfDeposit.summary.line2', {
            depositShare: savings.depositSharePct.toFixed(2),
            interestShare: savings.interestSharePct.toFixed(2),
            interestAmount: formatCr(savings.interestCr),
          }),
        ]}
      />
    </div>
  )
}

export default BankingDepositsCostOfDepositPage
